import { datadogRum } from '@datadog/browser-rum-slim'
import { createRouter, createWebHistory, createMemoryHistory } from 'vue-router'
import { route as defineRouter } from 'quasar/wrappers'
import { organizationSubdomain } from 'src/utils/domains'
// Layout components
const LoggedIn = () => import("../layouts/LoggedIn.vue");
const Default = () => import("../layouts/Default.vue");
const ComputedLayout = () => import("../layouts/ComputedLayout.vue");
const StudentLayout = () => import("../layouts/StudentLayout.vue");

// Main views
const DashboardView = () => import("../views/DashboardView.vue");
const Login = () => import("../views/Login.vue");
const DemoAccountView = () => import("../views/DemoAccountView.vue");
const StudentPayment = () => import("../views/StudentPayment.vue");
const Verification = () => import("../views/Verification.vue");
const RequestPasswordReset = () => import("../views/RequestPasswordReset.vue");
const Features = () => import("../views/Features.vue");
const ProgramPage = () => import("../views/ProgramPage.vue");
const Pricing = () => import("../views/Pricing.vue");
const ContactUs = () => import("../views/ContactUs.vue");
const SignUp = () => import("../views/SignUp.vue");
const Profile = () => import("../views/Profile.vue");
const NewPassword = () => import("../views/NewPassword.vue");
const OrganizationView = () => import("../views/OrganizationView.vue");
const Logout = () => import("../views/Logout.vue");
const BookstorePurchase = () => import("../views/BookstorePurchase.vue");

// Course related
const Courses = () => import("../views/Courses.vue");
const Course = () => import("../views/Course.vue");
const Activity = () => import("../views/Activity.vue");

// Flowsheet related
const Flowsheets = () => import("../views/Flowsheets.vue");
const FlowsheetBuilder = () => import("../views/FlowsheetBuilder.vue");

// Student related
const StudentActivity = () => import("../views/student/StudentActivity.vue");
const StudentContentViewer = () => import("../views/student/StudentContentViewer.vue");
const StudentDashboard = () => import("../views/student/StudentDashboard.vue");
const StudentActivities = () => import("../views/student/StudentActivities.vue");

// Patient related
const PatientTemplates = () => import("../views/patient/PatientTemplates.vue");
const PatientDetails = () => import("../views/patient/PatientDetails.vue");

// Chart related
const Charts = () => import("../views/Charts.vue");
const Versions = () => import("../views/Versions.vue");
const Version = () => import("../views/Version.vue");
const Chart = () => import("../views/Chart.vue");

// Other views
const Campus = () => import("../views/Campus.vue");
const Program = () => import("../views/Program.vue");
const Licenses = () => import("../views/Licenses.vue");

// Components
const AdvancedEdit = () => import("../components/flowsheets/AdvancedEdit.vue");

// Keep this as is since it's likely a module with route configurations
import { patient_routes } from "./patient";
import { guardProgramSlug, programTitle, programDescription } from "./programGuard";
import { HOME_TITLE, HOME_DESCRIPTION } from "./marketingMeta";
// Keep store import as is since it might be needed for initialization
import { useUserStore } from "../stores/user";
import { useRegionStore } from "src/stores/region";
import { storeToRefs } from "pinia";
import { Notify, useQuasar } from "quasar";
import { isSafeInternalPath } from "./safePath";
import {
  isStaleChunkError,
  shouldAttemptStaleChunkReload,
  clearStaleChunkReloadGuard
} from "./staleChunk";

const Cohort = () => import('../views/Cohort.vue')
const Cohorts = () => import('../views/Cohorts.vue')
const SchoolsView = () => import('../views/SchoolsView.vue');
const UsersView = () => import('../views/UsersView.vue');
const HandleUpload = () => import('../views/HandleUpload.vue');
const SimControlPanel = () => import('../views/SimControlPanel.vue');
const StripePayment = () => import('../views/StripePayment.vue');
const Tour = () => import('../views/Tour.vue');
const Devices = () => import('../views/Devices.vue')

// Route table + options are module-level; the router INSTANCE is built per
// call (createAppRouter below) so SSR gets a fresh router per request.
const routerOptions = {
  routes: [
    {
      path: '/',
      name: 'marketing',
      component: () => import('src/layouts/Marketing.vue'),
      children: [
        {
          path: '',
          name: 'home_view',
          component: () => import('src/views/IndexPage.vue'),
          meta: { title: HOME_TITLE, description: HOME_DESCRIPTION }
        },
        {
          path: '/contact',
          name: 'contact',
          component: ContactUs,
          meta: {
            title: 'Contact Us — ChartFlow',
            description: 'Contact the ChartFlow team to ask about pricing, schedule a demo, or get help with an academic EHR for your healthcare program.'
          }
        },
        {
          path: '/tour',
          name: 'tour',
          component: Tour,
          meta: {
            title: 'Tour — ChartFlow',
            description: 'Take a quick tour of ChartFlow, the academic EHR for nursing, allied health, and simulation programs, and see the patient chart in action.'
          }
        },
        {
          path: '/privacy',
          name: 'privacy',
          component: () => import('../views/Privacy.vue'),
          meta: {
            title: 'Privacy Policy — ChartFlow',
            description: 'How ChartFlow collects, uses, and protects the information of the students, instructors, and institutions who use our academic EHR.'
          }
        },
        {
          path: '/features',
          name: 'features',
          component: Features,
          meta: {
            title: 'Features — ChartFlow',
            description: 'Every ChartFlow feature in one place: realistic patient charts, flowsheets, eMAR, simulation control, and course tools for healthcare education.'
          }
        },
        {
          path: '/programs/:slug',
          name: 'marketing_program',
          component: ProgramPage,
          beforeEnter: guardProgramSlug,
          // Functions of the route: the text comes from data/marketingPrograms.js.
          meta: { title: programTitle, description: programDescription }
        },
        {
          path: '/terms',
          name: 'terms',
          component: () => import('../views/Terms.vue'),
          meta: {
            title: 'Terms of Service — ChartFlow',
            description: 'The terms of service for using ChartFlow, the academic EHR for healthcare education and simulation.'
          }
        },
        {
          path: '/accessibility',
          name: 'accessibility',
          component: () => import('../views/Accessibility.vue'),
          meta: {
            title: 'Accessibility Statement | ChartFlow — Accessible Healthcare Simulation & Educational EHR',
            description: 'ChartFlow is committed to accessible healthcare education. Our educational EHR and clinical simulation platform follows WCAG 2.2 AA standards to ensure every nursing, allied health, and CTE student can learn without barriers.'
          }
        }
      ]
    },
    {
      path: '/',
      name: 'home',
      component: Default,
      children: [
        {
          path: 'login',
          name: 'login',
          component: Login
        },
        {
          path: 'demo',
          name: 'demo',
          component: DemoAccountView,
          props: function (route) {
            return {
              verify: route.query.verify,
              email: route.query.email
            }
          }
        },
        {
          path: 'student_pay',
          name: 'student_pay',
          component: StudentPayment,
          meta: {
            requiresAuth: true,
            requiresLicense: false
          }
        },
        {
          path: 'stripe_payment',
          name: 'stripe_payment',
          component: StripePayment,
          props: function (route) {
            return {
              price_id: route.query.price_id,
            }
          }
        },
        {
          path: 'payment_complete',
          name: 'payment_complete',
          component: () => import('../views/PaymentComplete.vue'),
          props: function (route) {
            return {
              session_id: route.query.session_id,
            }
          }
        },
        {
          path: 'verification',
          name: 'verification',
          component: Verification,
          props: function (route) {
            return {
              verify: route.query.verify,
              email: route.query.email
            }
          }
        },
        {
          path: 'request_reset',
          name: 'request_reset',
          component: RequestPasswordReset
        },
        {
          path: '/logout',
          name: 'logout',
          component: Logout,
          meta: {
            requiresAuth: true,
            requiresLicense: false
          }
        },
        {
          path: 'pricing',
          name: 'pricing,',
          component: Pricing
        },
        {
          path: 'signup',
          name: 'signup',
          component: SignUp,
          props: function (route) {
            return {
              verify: route.query.verify,
              email: route.query.email,
              redirect_to: route.query.redirect_to
            }
          }
        },

        {
          path: 'maintenance',
          name: 'maintenance',
          component: () => import('../views/Maintenance.vue')
        }
      ]
    },
    {
      path: '/profile',
      name: 'computedlayout',
      meta: { requiresAuth: true, requiresLicense: false },
      component: ComputedLayout,
      children: [
        {
          path: '',
          name: 'profile',
          component: Profile
        },
        {
          path: '/undelete',
          name: 'undelete',
          component: () => import('../views/Undelete.vue')
        },
        {
          path: '/whats-new',
          name: 'whats_new',
          component: () => import('../views/WhatsNew.vue')
        },
        {
          path: '/new_password',
          name: 'new_password',
          component: NewPassword
        },
        {
          path: '/template_patients',
          name: 'template_patients',
          component: PatientTemplates,
          meta: { deny_students: true }
        },
        {
          path: '/template_providers',
          name: 'template_providers',
          component: () => import('../views/patient/ProviderTemplates.vue'),
          // `deny_students` is what keeps students out. Omitting meta entirely
          // would NOT make the route non-public — see the route-meta convention.
          meta: { deny_students: true }
        },
        {
          path: '/template_provider/new',
          name: 'template_provider_new',
          component: () => import('../views/patient/ProviderEditor.vue'),
          props: { uid: 'new' },
          meta: { deny_students: true }
        },
        {
          path: '/template_provider/:uid',
          name: 'template_provider',
          component: () => import('../views/patient/ProviderEditor.vue'),
          props: true,
          meta: { deny_students: true }
        },
        {
          path: '/template_activities',
          name: 'template_activities',
          component: () => import('../views/ActivityTemplates.vue')
        },
        {
          path: '/template_courses',
          name: 'template_courses',
          component: () => import('../views/CourseTemplates.vue')
        },
        {
          path: '/rubric_templates',
          name: 'rubric_templates',
          component: () => import('../views/RubricTemplates.vue'),
          meta: { deny_students: true }
        },
        {
          path: '/rubric_template/new',
          name: 'rubric_template_new',
          component: () => import('../views/RubricTemplateEditor.vue'),
          props: { uid: 'new' },
          meta: { deny_students: true }
        },
        {
          path: '/rubric_template/:uid',
          name: 'rubric_template',
          component: () => import('../views/RubricTemplateEditor.vue'),
          props: true,
          meta: { deny_students: true }
        },
        {
          path: '/new_patient',
          name: 'new_patient',
          component: PatientDetails,
          props: function (route) {
            return {
              template: route.query.template,
              is_new: true
            }
          }
        },
        {
          path: '/organization/:organization_uid/new_campus',
          name: 'new_campus',
          component: Campus,
          props: function (route) {
            return {
              is_new: true,
              organization_uid: route.params.organization_uid
            }
          }
        },
        {
          path: '/organization/:organization_uid/campus/:campus_uid',
          name: 'campus',
          component: Campus,
          props: function (route) {
            return {
              is_new: false,
              organization_uid: route.params.organization_uid,
              campus_uid: route.params.campus_uid
            }
          }
        },
        {
          path: '/organization/:organization_uid/new_program',
          name: 'new_program',
          component: Program,
          props: function (route) {
            return {
              is_new: true,
              organization_uid: route.params.organization_uid
            }
          }
        },
        {
          path: '/organization/:organization_uid/program/:program_uid',
          name: 'program',
          component: Program,
          props: function (route) {
            return {
              is_new: false,
              organization_uid: route.params.organization_uid,
              program_uid: route.params.program_uid
            }
          },
        },
        {
          path: '/organization/:organization_uid/purchase_new',
          name: 'purchase_new',
          component: BookstorePurchase,
          props: function (route) {
            return {
              organization_uid: route.params.organization_uid,
              is_new: true
            }
          }
        },
        {
          path: '/organization/:organization_uid/purchase/:invoice_id/licenses',
          name: 'org_license',
          component: Licenses,
          props: function (route) {
            return {
              entity_type: 'organization',
              entity_uid: route.params.organization_uid,
              invoice_id: route.params.invoice_id
            }
          }
        },
        {
          path: '/organization/:organization_uid/:entity_type/:entity_uid/purchase/:invoice_id/licenses',
          name: 'license',
          component: Licenses,
          props: function (route) {
            return {
              entity_type: route.params.entity_type,
              entity_uid: route.params.entity_uid,
              invoice_id: route.params.invoice_id
            }
          }
        },
        {
          path: '/organization/:organization_uid/saml_config',
          name: 'saml_config',
          component: () => import('../views/SAML.vue'),
          props: function (route) {
            return {
              organization_uid: route.params.organization_uid
            }
          }
        },
        {
          path: '/organization/:organization_uid/card_parser/new',
          name: 'new_card_parser',
          component: () => import('../views/CardParser.vue'),
          props: function (route) {
            return {
              organization_uid: route.params.organization_uid,
              is_new: true
            }
          }
        },
        {
          path: '/organization/:organization_uid/card_parser/:card_parser_uid',
          name: 'card_parser',
          component: () => import('../views/CardParser.vue'),
          props: function (route) {
            return {
              organization_uid: route.params.organization_uid,
              card_parser_uid: route.params.card_parser_uid,
              is_new: false
            }
          }
        },
        {
          path: '/organization/:organization_uid/new_cabinet',
          name: 'new_cabinet',
          component: () => import('../views/CabinetAdmin.vue'),
          props: function (route) {
            return {
              is_new: true,
              organization_uid: route.params.organization_uid
            }
          }
        },
        {
          path: '/organization/:organization_uid/campus/:campus_uid/new_cabinet',
          name: 'new_campus_cabinet',
          component: () => import('../views/CabinetAdmin.vue'),
          props: function (route) {
            return {
              is_new: true,
              organization_uid: route.params.organization_uid,
              campus_uid: route.params.campus_uid
            }
          }
        },
        {
          path: '/organization/:organization_uid/cabinet/:cabinet_uid',
          name: 'cabinet_admin',
          component: () => import('../views/CabinetAdmin.vue'),
          props: function (route) {
            return {
              is_new: false,
              organization_uid: route.params.organization_uid,
              cabinet_uid: route.params.cabinet_uid
            }
          }
        },
        {
          path: '/cohort/:entity_type/:entity_uid/new_cohort',
          name: 'new_cohort',
          component: Cohort,
          props: function (route) {
            return {
              is_new: true,
              entity_type: route.params.entity_type,
              entity_uid: route.params.entity_uid
            }
          }
        },
        {
          path: '/cohort/:entity_type/:entity_uid/:cohort_uid',
          name: 'cohort',
          component: Cohort,
          props: function (route) {
            return {
              is_new: false,
              entity_type: route.params.entity_type,
              entity_uid: route.params.entity_uid,
              cohort_uid: route.params.cohort_uid
            }
          },
        },
        {
          path: '/lti_config/:entity_type/:entity_uid/new',
          name: 'lti_config_new',
          component: () => import('../views/LTIConfig.vue'),
          props: function (route) {
            return {
              entity_type: route.params.entity_type,
              entity_uid: route.params.entity_uid,
              client_id: route.params.client_id
            }
          }
        },
        {
          path: '/lti_config/:entity_type/:entity_uid/:client_id',
          name: 'lti_config',
          component: () => import('../views/LTIConfig.vue'),
          props: function (route) {
            return {
              entity_type: route.params.entity_type,
              entity_uid: route.params.entity_uid,
              client_id: route.params.client_id
            }
          }
        },
        {
          path: '/lti_deployment/:entity_type/:entity_uid/new',
          name: 'lti_deployment_new',
          component: () => import('../views/LTIDeployment.vue'),
          props: function (route) {
            return {
              entity_type: route.params.entity_type,
              entity_uid: route.params.entity_uid,
              organization_uid: route.query.organization_uid
            }
          }
        },
        {
          path: '/lti_deployment/:entity_type/:entity_uid/:integration_id',
          name: 'lti_deployment',
          component: () => import('../views/LTIDeployment.vue'),
          props: function (route) {
            return {
              entity_type: route.params.entity_type,
              entity_uid: route.params.entity_uid,
              integration_id: route.params.integration_id,
              organization_uid: route.query.organization_uid
            }
          }
        },
        {
          path: '/lti/link',
          name: 'lti_link_activity',
          component: () => import('../views/LTILinkActivity.vue'),
          // `deny_students` is what keeps students out — see the route-meta
          // convention on /template_providers. The launch handler already declines
          // to send a student here, but a student who follows the URL any other way
          // would otherwise reach a page whose whole purpose is an instructor action.
          meta: { requiresAuth: true, requiresLicense: false, deny_students: true }
        },
        {
          path: '/handle_upload',
          name: 'handle_upload',
          component: HandleUpload,
          props: function (route) {
            return {
              url: route.query.url,
              entity_type: route.query.entity_type,
              entity_uid: route.query.entity_uid,
              role: route.query.role
            }
          },
        }
      ]
    },
    {
      path: '/cabinet',
      component: StudentLayout,
      meta: { requiresAuth: true },
      children: [
        {
          // Where an untied cabinet computer sends a student after sign-in:
          // their own activities that use this cabinet.
          path: 'select/:cabinet_uid',
          name: 'cabinet_activity_picker',
          component: () => import('../views/student/CabinetActivityPicker.vue'),
          props: (route) => ({ cabinet_uid: route.params.cabinet_uid })
        },
        {
          path: 'course/:course_uid/activity/:activity_uid',
          name: 'cabinet_view',
          component: () => import('../views/student/CabinetView.vue'),
          props: (route) => ({
            activity_uid: route.params.activity_uid,
            course_uid: route.params.course_uid
          })
        },
        {
          // A cabinet computer's view of a sim: the computer's OWN cabinet,
          // which need not be the sim's (a sim may use several cabinets).
          path: 'course/:course_uid/activity/:activity_uid/cabinet/:cabinet_uid',
          name: 'cabinet_view_at_cabinet',
          component: () => import('../views/student/CabinetView.vue'),
          props: (route) => ({
            activity_uid: route.params.activity_uid,
            course_uid: route.params.course_uid,
            cabinet_uid: route.params.cabinet_uid
          })
        },
        {
          path: 'course/:course_uid/activity/:activity_uid/patient/:patient_uid',
          name: 'cabinet_patient',
          component: () => import('../views/student/CabinetPatientView.vue'),
          props: (route) => ({
            activity_uid: route.params.activity_uid,
            course_uid: route.params.course_uid,
            patient_uid: route.params.patient_uid
          })
        },
      ]
    },
    {
      path: '/student',
      name: 'student',
      component: StudentLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'student_dashboard',
          component: StudentDashboard
        },
        {
          path: 'course/:course_uid/activities',
          name: 'course_activities',
          component: StudentActivities,
          props: true
        },
        {
          path: 'course/:course_uid/activity/:activity_uid',
          name: 'student_activity',
          component: StudentActivity,
          props: function (route) {
            return {
              activity_uid: route.params.activity_uid,
              course_uid: route.params.course_uid,
              preview: route.query.preview
            }
          }
        },
        {
          path: 'course/:course_uid/activity/:activity_uid/content',
          name: 'student_content_viewer',
          component: StudentContentViewer,
          props: function (route) {
            return {
              activity_uid: route.params.activity_uid,
              course_uid: route.params.course_uid
            }
          }
        },
        {
          path: 'course/:course_uid/activity/:activity_uid/create_patient',
          name: 'create_patient',
          component: PatientDetails,
          props: function (route) {
            return {
              is_new: true,
              course_uid: route.params.course_uid,
              activity_uid: route.params.activity_uid,
              name: route.query.name,
              template_patient_id: route.query.template_patient_id,
              simulation: route.query.simulation
            }
          }
        },
        {
          path: 'course/:course_uid/activity/:activity_uid/grading-report',
          name: 'student_grading_report',
          component: () => import('../views/GradingReportPage.vue'),
          props: function (route) {
            return {
              course_uid: route.params.course_uid,
              activity_uid: route.params.activity_uid,
              patient_uid: route.query.patient_uid,
              student_uid: route.query.student_uid,
              readonly: true
            }
          }
        }
      ]
    },
    {
      path: '/dashboard',
      name: 'dashboard_group',
      component: ComputedLayout,
      meta: { requiresAuth: true, deny_students: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: DashboardView
        },
        {
          path: '/courses',
          name: 'courses',
          component: Courses,
          props: function(route) {
              return {
                only_instructor: true
              }
          }
        },
        {
          path: '/course/:course_uid',
          name: 'course',
          component: Course,
          props: function (route) {
            return {
              course_uid: route.params.course_uid,
              added: route.query.added
            }
          }
        },
        {
          path: '/course_new',
          name: 'course_new',
          component: Course,
          props: function (route) {
            return {
              is_new: true,
              organization_uid: route.query.organization_uid,
              program_uid: route.query.program_uid,
              campus_uid: route.query.campus_uid,
              template: route.query.template
            }
          }
        },
        {
          path: '/new_activity',
          name: 'new_activity',
          component: Activity,
          props: function (route) {
            return {
              is_new: true,
              course_uid: route.query.course_uid,
              template: route.query.template
            }
          }
        },
        {
          path: '/course/:course_uid/activity/:uid',
          name: 'activity',
          component: Activity,
          props: true
        },
        {
          path: '/course/:course_uid/activity/:activity_uid/submissions/:user_uid',
          name: 'submissions_detail',
          component: () => import('../views/SubmissionsDetailPage.vue'),
          props: function (route) {
            return {
              course_uid: route.params.course_uid,
              activity_uid: route.params.activity_uid,
              user_uid: route.params.user_uid,
              attempt: route.query.attempt
            }
          },
          meta: { requiresAuth: true, deny_students: true }
        },
        {
          path: '/course/:course_uid/activity/:activity_uid/grading-report',
          name: 'grading_report',
          component: () => import('../views/GradingReportPage.vue'),
          props: function (route) {
            return {
              course_uid: route.params.course_uid,
              activity_uid: route.params.activity_uid,
              patient_uid: route.query.patient_uid,
              student_uid: route.query.student_uid,
              readonly: route.query.readonly === 'true'
            }
          },
          meta: { requiresAuth: true }
        },
        {
          path: '/course/:course_uid/activity/:activity_uid/content-grading-report',
          name: 'content_grading_report',
          component: () => import('../views/ContentGradingReportPage.vue'),
          props: function (route) {
            return {
              course_uid: route.params.course_uid,
              activity_uid: route.params.activity_uid,
              student_uid: route.query.student_uid
            }
          },
          meta: { requiresAuth: true, deny_students: true }
        },
        {
          path: '/devices',
          name: 'devices',
          component: Devices
        }
      ]
    },
    {
      path: '/organization/:uid',
      name: 'Manage Organization',
      component: ComputedLayout,
      meta: { requiresAuth: true, org_admin: true },
      children: [
        {
          path: '',
          name: 'organization_view',
          component: OrganizationView,
          props: function (route) {
            return {
              organization_uid: route.params.uid,
              is_new: false
            }
          }
        },
        {
          path: 'user/:user_uid',
          name: 'organization_user',
          component: Profile,
          props: function (route) {
            return {
              uid: route.params.user_uid
            }
          }
        }
      ]
    },
    {
      path: '/superuser',
      name: 'superuser',
      component: ComputedLayout,
      meta: { requiresAuth: true, superuser: true },
      children: [
        {
          path: '/user',
          name: 'users',
          component: UsersView
        },
        {
          path: '/user/:uid',
          name: 'user',
          component: Profile,
          props: true
        },
        {
          path: '/organization',
          name: 'organizations',
          component: SchoolsView
        },
        {
          path: '/organization_new',
          name: 'New Organization',
          component: OrganizationView,
          props: function (route) {
            return {
              bookstore: route.query.bookstore,
              is_new: true,
              organization_uid: null
            }
          }
        },
        {
          path: '/flowsheets',
          name: 'Flowsheets',
          // Superuser or Content Manager — NOT Support, so this overrides the
          // /superuser parent's Superuser/Support pair. Matches the builder
          // pages these link into.
          meta: { roles: ['Superuser', 'Content Manager'] },
          component: Flowsheets
        },
        {
          path: '/lookups',
          name: 'LookupTypes',
          // Superuser or Content Manager — NOT Support, so this overrides the
          // /superuser parent's Superuser/Support pair. Matches the builder
          // pages these link into.
          meta: { roles: ['Superuser', 'Content Manager'] },
          component: () => import('../views/LookupTypes.vue')
        },
        {
          path: '/lookups/:lookup_type',
          name: 'Lookups',
          // Superuser or Content Manager — NOT Support, so this overrides the
          // /superuser parent's Superuser/Support pair. Matches the builder
          // pages these link into.
          meta: { roles: ['Superuser', 'Content Manager'] },
          component: () => import('src/views/Lookups.vue'),
          props: function (route) {
            return {
              lookup_type_uid: route.params.lookup_type,
              version: route.query.version,
              from: route.query.from
            }
          }
        },
        {
          path: '/lookups/:lookup_type/edit/:edit_uid',
          name: 'LookupEdit',
          // The advanced editor for an action or a field of a lookup type, shown
          // inside the Lookup Builder page. Same roles as the page itself.
          meta: { roles: ['Superuser', 'Content Manager'] },
          component: () => import('src/views/Lookups.vue'),
          props: function (route) {
            return {
              lookup_type_uid: route.params.lookup_type,
              edit_uid: route.params.edit_uid,
              version: route.query.version,
              from: route.query.from
            }
          }
        },
        {
          path: '/charts',
          name: 'charts',
          component: Charts
        },
        {
          path: '/versions',
          name: 'Versions',
          component: Versions
        },
        {
          path: '/new_version',
          name: 'New Version',
          component: Version,
          props: function () {
            return {
              version_uid: 'new',
            }
          }
        },
        {
          path: '/version/:version_uid',
          name: 'Edit Version',
          component: Version,
          props: true,
        },
        {
          path: '/new_chart',
          name: 'New Chart',
          component: Chart,
          props: function (route) {
            return {
              chart_uid: 'new',
              version: route.query.version,
            }
          }
        },
        {
          path: '/chart/:version/:chart_uid',
          name: 'Edit Chart',
          component: Chart,
          props: function (route) {
            return {
              chart_uid: route.params.chart_uid,
              version: route.params.version,
              from: route.query.from
            }
          }
        },
        {
          path: '/feature-flags',
          name: 'feature_flags',
          component: () => import('../views/FeatureFlags.vue')
        },
        {
          path: '/admin/environment-sync',
          name: 'environment_sync',
          component: () => import('../components/admin/EnvironmentSyncManager.vue')
        },
        {
          path: '/admin/sync-history',
          name: 'sync_history',
          component: () => import('../components/admin/SyncHistoryViewer.vue')
        },
        {
          path: '/changelog-admin',
          name: 'changelog_admin',
          component: () => import('../views/ChangelogAdmin.vue')
        },
        {
          path: '/changelog-entry/new',
          name: 'changelog_entry_new',
          component: () => import('../views/ChangelogEntry.vue'),
          props: { uid: 'new' }
        },
        {
          path: '/changelog-entry/:uid',
          name: 'changelog_entry',
          component: () => import('../views/ChangelogEntry.vue'),
          props: true
        },
      ]
    },
    {
      path: '/flowsheet_builder/:version/:flowsheet_uid',
      name: 'flowsheet_builder',
      // Authoring tool: Superuser or Content Manager only. These routes carried
      // no meta at all, so the guard skipped them entirely — any logged-in user,
      // including a student, could open the builder by URL.
      meta: { requiresAuth: true, roles: ['Superuser', 'Content Manager'] },
      component: FlowsheetBuilder,
      props: function (route) {
        return {
          flowsheet_uid: route.params.flowsheet_uid,
          version: route.params.version,
          from: route.query.from
        }
      }
    },
    {
      path: '/flowsheet_builder/:version/:flowsheet_uid/advanced/:field_uid',
      name: 'flowsheet_builder_advanced',
      // Authoring tool: Superuser or Content Manager only. These routes carried
      // no meta at all, so the guard skipped them entirely — any logged-in user,
      // including a student, could open the builder by URL.
      meta: { requiresAuth: true, roles: ['Superuser', 'Content Manager'] },
      component: AdvancedEdit,
      props: function (route) {
        return {
          flowsheet_uid: route.params.flowsheet_uid,
          version: route.params.version,
          field_uid: route.params.field_uid,
          from: route.query.from
        }
      }
    },
    {
      path: '/flowsheet_builder/:version/:flowsheet_uid/multiple/:field_uid',
      name: 'flowsheet_builder_multiple',
      // Authoring tool: Superuser or Content Manager only. These routes carried
      // no meta at all, so the guard skipped them entirely — any logged-in user,
      // including a student, could open the builder by URL.
      meta: { requiresAuth: true, roles: ['Superuser', 'Content Manager'] },
      component: AdvancedEdit,
      props: function (route) {
        return {
          flowsheet_uid: route.params.flowsheet_uid,
          version: route.params.version,
          field_uid: route.params.field_uid,
          from: route.query.from
        }
      }
    },
    {
      path: '/flowsheet_builder/:version/:flowsheet_uid/advanced/:field_uid/action/:action_uid',
      name: 'flowsheet_builder_action',
      // Authoring tool: Superuser or Content Manager only. These routes carried
      // no meta at all, so the guard skipped them entirely — any logged-in user,
      // including a student, could open the builder by URL.
      meta: { requiresAuth: true, roles: ['Superuser', 'Content Manager'] },
      component: AdvancedEdit,
      props: function (route) {
        return {
          flowsheet_uid: route.params.flowsheet_uid,
          version: route.params.version,
          field_uid: route.params.field_uid,
          action_uid: route.params.action_uid,
          from: route.query.from
        }
      }
    },
    {
      path: '/new_flowsheet',
      name: 'new_flowsheet',
      // Authoring tool: Superuser or Content Manager only. These routes carried
      // no meta at all, so the guard skipped them entirely — any logged-in user,
      // including a student, could open the builder by URL.
      meta: { requiresAuth: true, roles: ['Superuser', 'Content Manager'] },
      component: FlowsheetBuilder,
      props: function (route) {
        return {
          flowsheet_uid: 'new',
          version: route.query.version,
        }
      }
    },
    {
      path: '/sim',
      name: 'sim',
      component: LoggedIn,
      meta: { requiresAuth: true, deny_students: true },
      children: [
        {
          path: 'course/:course_uid/activity/:uid',
          name: 'sim_control_panel',
          component: SimControlPanel,
          props: true
        }

      ]
    },
    {
      path: '/catalog',
      name: 'catalog_group',
      component: () => import('src/layouts/Marketing.vue'),
      children: [
        {
          path: '',
          name: 'catalog',
          component: () => import('../views/CatalogPage.vue')
        },
        {
          path: 'course/:course_uid',
          name: 'catalog_course_detail',
          component: () => import('../views/CatalogDetailPage.vue'),
          props: true
        },
        {
          path: 'bundle/:bundle_uid',
          name: 'catalog_bundle_detail',
          component: () => import('../views/BundleDetailPage.vue'),
          props: true
        },
        {
          path: 'course/:course_uid/checkout',
          name: 'catalog_checkout',
          component: () => import('../views/CatalogCheckoutPage.vue'),
          props: true
        }
      ]
    },
    ...patient_routes,
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFound.vue')
    }
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      if (to.hash) {
        return {
          el: to.hash,
          behavior: 'smooth'
        }
      }
      return { top: 0 }
    }
  },
}

const beforeEachGuard = async (to, from, next) => {
  // SSR (build-time prerender of public pages): no session, no window, no
  // localStorage. The guard's work (session restore, "/" -> default route,
  // org-subdomain "/" -> /login, requiresAuth) is all re-run in the browser
  // on the first client navigation, so the server just lets the route render.
  if (import.meta.env.SSR) {
    next();
    return;
  }
  const userStore = useUserStore();
  const regionStore = useRegionStore();
  const { org } = storeToRefs(regionStore);
  const $q = useQuasar()

  if (to.path === '/login') {
    next();
    return;
  }


  datadogRum.setUser({
    id: userStore.user ? userStore.user.uid : null,
    name: userStore.user ? userStore.user.username : null,
    orgname: org.value ? org.value.name : null,
    org_uid: org.value ? org.value.uid : null,
    role: userStore.user ? userStore.user_role : null
  });
  datadogRum.setAccount({
    id: org.value ? org.value.uid : null,
    name: org.value ? org.value.name : null
  })

  if ((userStore.loggedIn || to.query.logged_in) && !userStore.user) {
    $q.loading.show();
    try {
      const result = await userStore.getUserSession();
      if (result !== true) {
        // Clear stale localStorage if session fetch fails
        localStorage.removeItem('logged_in');
        userStore.sessionStore.loggedIn = false;
        // Deliberately no next() here — fall through. A failed session restore
        // means the visitor is ANONYMOUS, not forbidden, and the checks below
        // already know what to do with an anonymous visitor: the requiresAuth
        // branch sends them to /login with a redirect_to, and a public page
        // just renders. Redirecting unconditionally here is what threw
        // stale-session visitors off the marketing pages (#936).
      }
    }
    catch (error) {
      // Clear stale localStorage on session fetch error
      localStorage.removeItem('logged_in');
      userStore.sessionStore.loggedIn = false;
      // Falls through for the same reason as above.
    }
    finally {
      $q.loading.hide();
    }
  }

  if (to.path === '/') {
    if (userStore.loggedIn) {
      next({ path: userStore.default_route })
      return;
    }
    else {
      if (to.path === '/' && organizationSubdomain(window.location.hostname)) {
        next({ path: '/login' })
        return;
      }
    }
  }


  if (to.matched.some(record => record.meta.requiresAuth) && !userStore.loggedIn) {
    next({ path: '/login', query: { redirect_to: to.fullPath } })
    return;
  } else {
    if (to.matched.some(record => record.meta.requiresAuth) && to.matched.some(record => record.meta.roles)) {
      // Explicit role allow-list. Checked BEFORE `superuser` on purpose: the
      // flowsheet pages sit under the /superuser parent but their own role set
      // is Superuser or Content Manager — NOT Support. The nearest `roles` in
      // the matched chain therefore has to override the parent's meta rather
      // than be short-circuited by it.
      const allowed = to.matched.filter(record => record.meta.roles).pop().meta.roles;
      if (!userStore.user.roles?.some(role => allowed.includes(role))) {
        next(userStore.default_route)
        return;
      }
      next()
      return;
    } else if (to.matched.some(record => record.meta.requiresAuth) && to.matched.some(record => record.meta.superuser) && !userStore.user.roles?.some(role => ['Superuser', 'Support'].includes(role))) {
      next(userStore.default_route)
      return;
    } else if (to.matched.some(record => record.meta.requiresAuth) && to.matched.some(record => record.meta.org_admin)) {
      // Allow Superuser/Support OR Organization Admin for the specific org
      const orgUid = to.params.uid;
      const isSuperuserOrSupport = userStore.user.roles?.some(role => ['Superuser', 'Support'].includes(role));
      const isOrgAdmin = orgUid && userStore.check_roles(['Organization Admin'], orgUid);
      if (!isSuperuserOrSupport && !isOrgAdmin) {
        next(userStore.default_route)
        return;
      }
      next()
      return;
    } else {
      if (org.value && org.value.billing_level !== 'Free' && org.value.billing_level !== 'B2C' && to.matched.some(record => record.meta.requiresAuth && record.meta.requiresLicense !== false) && userStore.default_route === '/student') {
        if (!userStore.check_license()) {
          next('/student_pay')
          return
        }
      }
      if (userStore.default_route === '/student' && to.matched.some(record => record.meta.deny_students)) {
        Notify.create({
          color: 'negative',
          message: 'Students are not allowed to access this page',
          icon: 'error'
        })
        next({ path: userStore.default_route })
      }
      next()
    }
  }
};

export { isStaleChunkError, shouldAttemptStaleChunkReload, clearStaleChunkReloadGuard }

export { isSafeInternalPath }

// Handle TipTap/ProseMirror unmount errors during route navigation.
// ProseMirror destroys its node views before Vue can unmount them,
// causing "Cannot read properties of null" errors. These are harmless
// and should not block navigation.
const onRouterError = (error, to) => {
  if (error.message?.includes("Cannot read properties of null")) {
    console.warn('Router: suppressed TipTap unmount error during navigation')
    return
  }
  if (isStaleChunkError(error)) {
    // Load the route the user was actually navigating to, not the one they
    // are standing on. A reload() re-fetches the old URL, so they lose the
    // navigation as well as their in-page state and land back where they
    // started with no idea why; the click they made simply did nothing. The
    // document load is what fixes the problem either way — it is the only way
    // to pick up the new index.html — so it costs nothing to land on the
    // right page. CloudFront's SPA fallback serves index.html for any path,
    // so the deep link resolves.
    //
    // ...but only if that target is certainly a path on this origin. When it
    // is not, fall back to the document's own path, which is same-origin by
    // construction, rather than navigating somewhere we cannot vouch for.
    const attempted = to?.fullPath
    // Hash included so this reads the same as a vue-router fullPath: the
    // afterEach below clears the guard by fullPath, and a key that cannot be
    // matched there is a key that never gets re-armed.
    const currentPath = `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`
    let path = currentPath
    if (isSafeInternalPath(attempted)) {
      path = attempted
    } else if (attempted) {
      console.warn(
        'Router: refusing to recover onto a target that may not be same-origin',
        attempted,
        '- using',
        currentPath,
        'instead'
      )
    }
    if (isSafeInternalPath(path) && shouldAttemptStaleChunkReload(path)) {
      console.warn('Router: stale asset chunk after deploy, reloading', path, error)
      window.location.assign(path)
      return
    }
    // Either the reload already happened and did not help, or we have no
    // bounded/safe way to attempt one. Rethrow rather than swallow: the
    // navigation is dead either way, and a silent return would leave the user
    // on a frozen page with nothing in Datadog to show why. Note this rethrows
    // the ORIGINAL stale-chunk error — a storage failure inside the guard is
    // logged there and never allowed to replace it.
  }
  throw error
}

// The guard above has to survive the document load it triggers, which is
// exactly why it cannot be write-once: nothing else in the app clears these
// keys, so before this hook a *successful* recovery left the key behind and
// the next deploy hit a hard crash for that path with no retry — the failure
// mode belongs to precisely the long-lived tabs this handler exists to serve.
//
// A completed navigation is the signal that recovery worked, and it is a
// stronger one than "the app booted". vue-router awaits a lazy route component
// during the resolve phase, so a chunk that is still missing throws into
// router.onError and the navigation never confirms; afterEach runs only once
// that chunk actually loaded. Clearing on boot instead would fire before the
// route resolves and re-arm the reload in time for the same failure — the
// infinite loop the guard exists to prevent.
//
// Why the loop still cannot run: window.location.assign(P) requires
// shouldAttemptStaleChunkReload(P), which requires the key for P to be absent
// and immediately sets it. The only thing that removes it is a completed
// navigation to P. So between any two reloads of P there must be a successful
// load of P — and a reload loop is by definition a run of reloads with only
// failures in between. Clearing is deliberately per-path, not a sweep of every
// key: a document serving a stale index.html can still resolve routes whose
// chunk hashes did not change, and letting that clear an unrelated path's
// guard would hand the dead path another reload it cannot survive.
const clearStaleGuardAfterEach = (to) => {
  clearStaleChunkReloadGuard(to.fullPath)
}

const titleAndFocusAfterEach = (to) => {
  if (import.meta.env.SSR) return  // document.title / focus handling are browser-only
  const titleMap = {
    'login': 'Log In — ChartFlow',
    'signup': 'Create Student Account — ChartFlow',
    'request_reset': 'Reset Password — ChartFlow',
    'profile': 'Profile — ChartFlow',
    'student_dashboard': 'Student Dashboard — ChartFlow',
    'student_activities': 'Activities — ChartFlow',
    'student_activity': 'Activity — ChartFlow',
    'patient_layout': 'Patient Chart — ChartFlow',
    'patient_home': 'Patient Chart — ChartFlow',
    'visit_history': 'Visit History — ChartFlow',
    'courses': 'Courses — ChartFlow',
    'course': 'Course — ChartFlow',
    'activity': 'Activity — ChartFlow',
    'dashboard': 'Dashboard — ChartFlow',
    'logout': 'Log Out — ChartFlow',
    'verification': 'Verify Email — ChartFlow',
    'new_password': 'New Password — ChartFlow',
    'demo': 'Free Demo — ChartFlow',
    'pricing': 'Pricing — ChartFlow',
    'organizations': 'Organizations — ChartFlow',
    'organization': 'Organization — ChartFlow',
    'users': 'Users — ChartFlow',
    'flowsheets': 'Flowsheets — ChartFlow',
    'flowsheet_builder': 'Flowsheet Builder — ChartFlow',
    'charts': 'Visit Types — ChartFlow',
    'versions': 'Versions — ChartFlow',
    'sim_control': 'Simulation Control — ChartFlow',
    'new_activity': 'New Activity — ChartFlow',
    'grading_report': 'Grading Report — ChartFlow',
    'catalog': 'Catalog — ChartFlow',
    'catalog_checkout': 'Checkout — ChartFlow',
    'template_patients': 'Patient Templates — ChartFlow',
    'template_providers': 'Provider Templates — ChartFlow',
    'template_provider': 'Provider Template — ChartFlow',
    'template_provider_new': 'New Provider Template — ChartFlow',
    'template_activities': 'Activity Templates — ChartFlow',
    'template_courses': 'Course Templates — ChartFlow',
  }
  // Marketing-layout routes keep the title the layout set from their route meta
  // (layouts/Marketing.vue); setting it here too would overwrite the title the
  // prerendered page was served with.
  if (!to.matched.some(record => record.name === 'marketing')) {
    document.title = titleMap[to.name] || 'ChartFlow'
  }

  // Same-page hash links: move focus to the target element (CHA_53)
  if (to.hash) {
    setTimeout(() => {
      const target = document.querySelector(to.hash)
      if (target) {
        if (!target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1')
        }
        target.focus()
      }
    }, 300) // Allow scroll animation to complete
    return
  }

  // Reset focus to top of page on navigation so tab order is consistent.
  // Without this, SPAs leave focus wherever it was on the previous page.
  document.activeElement?.blur()
  document.body.setAttribute('tabindex', '-1')
  document.body.focus()
  document.body.removeAttribute('tabindex')
}

function createAppRouter() {
  const r = createRouter({
    // SSR (build-time prerender only): memory history, no window.
    history: import.meta.env.SSR ? createMemoryHistory() : createWebHistory(import.meta.env.BASE_URL),
    ...routerOptions
  })
  r.beforeEach(beforeEachGuard)
  r.onError(onRouterError)
  r.afterEach(clearStaleGuardAfterEach)
  r.afterEach(titleAndFocusAfterEach)
  return r
}

// The browser's one router, created when this module is first imported (as it
// always was), and the instance the stores' 401 redirect uses. Under SSR there
// is deliberately no module-level router: one server process renders many
// requests, and a router shared between them renders each page with the
// previous request's route. Server code gets a fresh router per render from the
// default export below, and nothing that needs this binding runs there.
export const router = import.meta.env.SSR ? null : createAppRouter()

// Quasar calls this once in the browser (it gets the router above) and once
// per request under SSR (it gets a new router with memory history).
export default defineRouter(() => (import.meta.env.SSR ? createAppRouter() : router))
